import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, Card, StatTile } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ShopifyClient } from "../api/shopify";
import { NexpimView } from "../views";

/**
 * What the product sync discovered about the store: publications, markets and
 * the catalog publication each one listens to, locales, the inventory
 * location, the app's scopes. Read once and cached server-side; the button
 * rediscovers after a market, a locale or a scope was added in the Shopify
 * admin. Nothing here is edited by hand — that is the point of discovering it.
 */
export function ShopifyStorePage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new ShopifyClient(http), [http]);
  const allowed = hasView(NexpimView.API_INVENTORY_UPDATES);

  const { data, isLoading, error } = useQuery({
    queryKey: ["shopify", "store"],
    queryFn: () => client.store(),
    enabled: allowed,
    staleTime: 60 * 60_000,
    retry: false,
  });
  const refresh = useMutation({
    mutationFn: () => client.refreshStore(),
    onSuccess: (info) => {
      queryClient.setQueryData(["shopify", "store"], info);
      toast.success(t("shopify.store.refreshed", "Store rediscovered"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("error.generic", "Request failed")),
  });

  const title = t("navigation.item.shopify-store", "Shopify store");
  if (!allowed) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t("shopify.store.subtitle", "What the product sync knows about the store: discovered from Shopify, not typed in.")}
        actions={
          <Button size="sm" icon={<RefreshCw className="size-4" />} disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {t("shopify.store.refresh", "Rediscover")}
          </Button>
        }
      />
      <div className="flex flex-col gap-4 p-4 lg:p-6">
        {isLoading && <p className="text-[13px] text-ink-faint">{t("loading", "Loading…")}</p>}
        {error && (
          <p className="text-[13px] text-risk">
            {error instanceof Error ? error.message : t("shopify.tab.unavailable", "Shopify could not be read.")}
          </p>
        )}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile label={t("shopify.store.shop", "Shop")} value={data.shop_name} />
              <StatTile label={t("shopify.store.currency", "Currency")} value={`${data.currency} (${data.presentment_currencies.join(", ")})`} />
              <StatTile label={t("shopify.store.api", "API version")} value={data.api_version} />
              <StatTile label={t("shopify.store.discovered", "Discovered")} value={new Date(data.discovered_at).toLocaleString()} />
            </div>

            <Card className="p-5">
              <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.store.markets", "Markets")}</h3>
              <table className="text-[13px] w-full">
                <thead className="text-ink-faint text-left">
                  <tr>
                    <th className="pr-4 font-medium">{t("column.name", "Name")}</th>
                    <th className="pr-4 font-medium">{t("shopify.store.handle", "Handle")}</th>
                    <th className="pr-4 font-medium">{t("shopify.store.currency", "Currency")}</th>
                    <th className="pr-4 font-medium">{t("shopify.store.publication", "Catalog publication")}</th>
                    <th className="pr-4 font-medium">{t("shopify.store.price-list", "Price list")}</th>
                    <th className="pr-4 font-medium">{t("shopify.store.root-url", "Storefront")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.markets.map((m) => (
                    <tr key={m.id}>
                      <td className="pr-4">
                        {m.name} {m.primary && <Badge size="sm">primary</Badge>} {!m.enabled && <Badge size="sm" variant="warning">disabled</Badge>}
                      </td>
                      <td className="pr-4 font-mono">{m.handle}</td>
                      <td className="pr-4">{m.currency}</td>
                      <td className="pr-4 font-mono text-[11px]">{m.publication_id ?? ""}</td>
                      <td className="pr-4">
                        {m.price_list_currency ?? ""}
                        {m.fixed_prices_count != null && ` · ${m.fixed_prices_count} ${t("shopify.store.fixed-prices", "fixed prices")}`}
                      </td>
                      <td className="pr-4 font-mono text-[11px]">{m.root_url ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[12px] text-ink-faint mt-2">
                {t("shopify.store.markets.hint", "One price in {{currency}}; Shopify converts for the other market. A market's price list should have no fixed prices.", { currency: data.currency })}
              </p>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-5">
                <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.store.publications", "Publications")}</h3>
                <ul className="text-[13px] space-y-1">
                  {data.publications.map((p) => (
                    <li key={p.id} className="flex items-center gap-2">
                      <span>{p.name}</span>
                      {p.id === data.online_store_publication_id && <Badge size="sm" variant="success">{t("shopify.store.managed", "managed")}</Badge>}
                      <span className="font-mono text-[11px] text-ink-faint">{p.id}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="p-5">
                <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("shopify.store.locales", "Locales")}</h3>
                <ul className="text-[13px] space-y-1">
                  {data.locales.map((l) => (
                    <li key={l.locale} className="flex items-center gap-2">
                      <span className="font-mono">{l.locale}</span>
                      <span>{l.name}</span>
                      {l.primary && <Badge size="sm">primary</Badge>}
                      {!l.published && <Badge size="sm" variant="warning">unpublished</Badge>}
                    </li>
                  ))}
                </ul>
                <h3 className="mb-2 mt-4 text-[13px] font-semibold text-ink">{t("shopify.store.locations", "Locations")}</h3>
                <ul className="text-[13px] space-y-1">
                  {data.locations.map((l) => (
                    <li key={l.id} className="flex items-center gap-2">
                      <span>{l.name}</span>
                      <span className="font-mono text-[11px] text-ink-faint">{l.id.substring(l.id.lastIndexOf("/") + 1)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[12px] text-ink-faint mt-2">
                  {t("shopify.store.locations.hint", "One Shopify sales channel per location: the channel's location id is the number shown here.")}
                </p>
                <a href={data.admin_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline text-[13px] mt-2">
                  {t("shopify.link.admin", "Shopify admin")} <ExternalLink className="size-3" />
                </a>
              </Card>
            </div>

            <Card className="p-5">
              <h3 className="mb-4 text-[13px] font-semibold text-ink">
                {t("shopify.store.scopes", "App scopes")}{" "}
                {data.missing_scopes.length === 0 ? (
                  <Badge size="sm" variant="success">{t("shopify.store.scopes.ok", "all required scopes granted")}</Badge>
                ) : (
                  <Badge size="sm" variant="error">{t("shopify.store.scopes.missing", "missing: {{scopes}}", { scopes: data.missing_scopes.join(", ") })}</Badge>
                )}
              </h3>
              <p className="text-[11px] text-ink-faint font-mono break-words">{data.access_scopes.join(" ")}</p>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
